import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, MedicalServices as MedicalIcon } from '@mui/icons-material';
import { medicalHistoryAPI } from '../services/api';

const categoryLabels = {
  systemic: 'Systemic',
  ocular: 'Ocular',
  allergy: 'Allergy',
  medication: 'Medication',
  other: 'Other',
};

const categoryColors = {
  systemic: 'primary',
  ocular: 'info',
  allergy: 'warning',
  medication: 'secondary',
  other: 'default',
};

const emptyForm = { name: '', category: 'systemic', isActive: true };

const MedicalHistoryMaster = () => {
  const [conditions, setConditions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchConditions = async () => {
    try {
      const res = await medicalHistoryAPI.getConditions({ includeInactive: true });
      setConditions(res.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch medical history master', 'error');
    }
  };

  useEffect(() => { fetchConditions(); }, []);

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setOpenDialog(true);
  };

  const openEdit = (condition) => {
    setForm({ name: condition.name, category: condition.category, isActive: condition.isActive });
    setEditingId(condition._id);
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showSnackbar('Please provide a condition name', 'error');
      return;
    }
    try {
      if (editingId) {
        await medicalHistoryAPI.updateCondition(editingId, form);
        showSnackbar('Condition updated');
      } else {
        await medicalHistoryAPI.createCondition(form);
        showSnackbar('Condition added');
      }
      setOpenDialog(false);
      fetchConditions();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to save condition', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await medicalHistoryAPI.deleteCondition(id);
      showSnackbar('Condition deleted');
      fetchConditions();
    } catch (error) {
      showSnackbar('Failed to delete condition', 'error');
    }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(124, 132, 212, 0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main'
          }}>
            <MedicalIcon />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight="700">Medical History Master</Typography>
            <Typography variant="body2" color="text.secondary">Conditions selectable during patient check-in</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Add Condition</Button>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 'none' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Condition</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conditions.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">No conditions configured yet</Typography>
                </TableCell></TableRow>
              ) : conditions.map((condition) => (
                <TableRow key={condition._id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{condition.name}</Typography></TableCell>
                  <TableCell>
                    <Chip size="small" label={categoryLabels[condition.category]} color={categoryColors[condition.category]} sx={{ fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={condition.isActive ? 'Active' : 'Inactive'} color={condition.isActive ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(condition)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(condition._id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Condition' : 'New Condition'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Condition Name *"
                placeholder="e.g. Diabetic, Cardiac Disease"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {Object.entries(categoryLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
                label="Active (visible in check-in form)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editingId ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MedicalHistoryMaster;
